import { useEffect, useRef, useState } from "react";
import {
  applyEvent,
  initialState,
  type DisplayEvent,
  type DisplayState,
  type ServerMessage,
} from "../shared/protocol.ts";

/**
 * Subscribes to the relay and folds incoming events into state.
 *
 * Same-origin by construction, so there is nothing to configure: in dev Vite
 * proxies /panel to the relay, in production Fastify serves this page itself.
 */

/**
 * Gemini uses parallel function calling: the tiles of one dashboard typically
 * arrive within milliseconds of each other. Without pacing, "build one by one"
 * collapses into a single pop. Live tiles therefore go through a reveal queue;
 * anything that replaces the screen wholesale (begin/clear/video) drops the
 * queue, and a `sync` (late joiner) always shows the full state at once.
 *
 * The interval is `config.revealMs` (v3), which is per-screen because the right
 * pacing depends on the room: a boardroom watching one dashboard wants the
 * build, a lobby panel people walk past wants it up.
 */

export function useDisplay(): { state: DisplayState; connected: boolean } {
  const [state, setState] = useState<DisplayState>(initialState);
  const [connected, setConnected] = useState(false);
  const seqRef = useRef(0);
  const queueRef = useRef<DisplayEvent[]>([]);
  const timerRef = useRef<number | undefined>(undefined);
  /**
   * Read by the drain loop, which lives outside React's render and must not
   * re-subscribe the socket when the pacing changes. A ref rather than a
   * dependency: putting `state.config.revealMs` in the effect's array would
   * tear down and rebuild the WebSocket every time the console saved a colour.
   */
  const revealMsRef = useRef(initialState.config.revealMs);
  revealMsRef.current = state.config.revealMs;

  useEffect(() => {
    const applyNow = (ev: DisplayEvent) =>
      setState((s) => applyEvent(s, ev));

    const dropQueue = () => {
      queueRef.current = [];
      window.clearTimeout(timerRef.current);
      timerRef.current = undefined;
    };

    const drain = () => {
      timerRef.current = undefined;
      const next = queueRef.current.shift();
      if (next) applyNow(next);
      if (queueRef.current.length) {
        timerRef.current = window.setTimeout(drain, revealMsRef.current);
      }
    };

    const enqueueTile = (ev: DisplayEvent) => {
      queueRef.current.push(ev);
      if (timerRef.current === undefined) drain();
    };

    const handle = (ev: DisplayEvent) => {
      switch (ev.type) {
        case "display.reload":
          // The console asked for this screen to refresh itself (§8.6). Do it
          // now; the fresh load re-syncs from the relay.
          window.location.reload();
          break;
        case "dashboard.tile":
          enqueueTile(ev);
          break;
        case "robot.status":
        case "screen.config":
          // Orthogonal to the screen content; must not disturb a reveal. A
          // config push mid-build re-themes the tiles already up and lets the
          // rest keep arriving.
          applyNow(ev);
          break;
        default:
          // begin / clear / video replace the screen: pending tiles belong to
          // the previous dashboard (or are superseded), so drop them.
          dropQueue();
          applyNow(ev);
      }
    };
    let ws: WebSocket | null = null;
    let retryTimer: number | undefined;
    let attempt = 0;
    let closed = false;

    const connect = () => {
      const proto = location.protocol === "https:" ? "wss" : "ws";
      // Which robot's room to watch. The screen is opened as
      // `.../?robot=<id>&screen=1`; with no `robot` param the relay puts us in
      // the default room (the v1 compatibility path).
      const robot = new URLSearchParams(location.search).get("robot");
      const qs = robot ? `?robot=${encodeURIComponent(robot)}` : "";
      ws = new WebSocket(`${proto}://${location.host}/panel${qs}`);

      ws.onopen = () => {
        attempt = 0;
        setConnected(true);
        // Tell the relay our viewport once, so the console's Screens page can
        // show whether this screen is big enough to read from across the room.
        ws?.send(
          JSON.stringify({
            type: "panel.hello",
            viewport: `${window.screen.width}x${window.screen.height}`,
          }),
        );
      };

      ws.onmessage = (e) => {
        let msg: ServerMessage;
        try {
          msg = JSON.parse(e.data as string);
        } catch {
          return;
        }

        if (msg.type === "sync") {
          seqRef.current = msg.seq;
          dropQueue();
          setState(msg.state);
          return;
        }

        // A gap means we missed an event; reconnecting is the cheapest correct
        // recovery since the relay replays full state on connect.
        if (msg.seq !== seqRef.current + 1) {
          ws?.close();
          return;
        }
        seqRef.current = msg.seq;
        handle(msg);
      };

      const reconnect = () => {
        setConnected(false);
        if (closed) return;
        // Cap backoff at 5s: this screen is unattended and must recover on its own.
        const delay = Math.min(5000, 300 * 2 ** attempt++);
        retryTimer = window.setTimeout(connect, delay);
      };
      ws.onclose = reconnect;
      ws.onerror = () => ws?.close();
    };

    connect();
    return () => {
      closed = true;
      dropQueue();
      window.clearTimeout(retryTimer);
      ws?.close();
    };
  }, []);

  return { state, connected };
}
